/**
 * GameOverScene - Displays game results and options
 * Shows score, high score, and retry/menu options
 */

import Phaser from 'phaser';
import { SCENES, COLORS, GAME } from '../config/gameConfig';

/**
 * Data passed to the GameOverScene
 */
export interface GameOverData {
  score: number;
  highScore: number;
  piecesPlaced: number;
  reason?: string;
  isWin?: boolean;
}

/**
 * Game over screen showing results
 */
export class GameOverScene extends Phaser.Scene {
  private gameData: GameOverData = {
    score: 0,
    highScore: 0,
    piecesPlaced: 0,
  };

  constructor() {
    super({ key: SCENES.GAME_OVER });
  }

  init(data: GameOverData): void {
    this.gameData = {
      score: data.score || 0,
      highScore: data.highScore || 0,
      piecesPlaced: data.piecesPlaced || 0,
      reason: data.reason || '',
      isWin: data.isWin || false,
    };
  }

  create(): void {
    this.cameras.main.setBackgroundColor(COLORS.BACKGROUND);

    const isNewHighScore = this.gameData.score >= this.gameData.highScore && this.gameData.score > 0;

    this.createTitle(isNewHighScore);
    this.createScoreDisplay(isNewHighScore);
    this.createReasonText();
    this.createStats();
    this.createButtons();
  }

  /**
   * Create the title text
   */
  private createTitle(isNewHighScore: boolean): void {
    let title: string;
    let color: string;

    if (this.gameData.isWin) {
      title = 'ZENITH REACHED!';
      color = '#4CAF50';
    } else if (isNewHighScore) {
      title = 'NEW HIGH SCORE!';
      color = '#FF9800';
    } else {
      title = 'GAME OVER';
      color = '#2D3436';
    }

    const titleText = this.add.text(GAME.WIDTH / 2, 180, title, {
      fontFamily: 'Arial',
      fontSize: '32px',
      color,
      fontStyle: 'bold',
    });
    titleText.setOrigin(0.5);

    // Animate title
    if (this.gameData.isWin || isNewHighScore) {
      this.tweens.add({
        targets: titleText,
        scaleX: 1.1,
        scaleY: 1.1,
        duration: 500,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  /**
   * Create score display
   */
  private createScoreDisplay(isNewHighScore: boolean): void {
    // Main score
    const scoreText = this.add.text(GAME.WIDTH / 2, 280, this.gameData.score.toString(), {
      fontFamily: 'Arial',
      fontSize: '72px',
      color: '#2D3436',
      fontStyle: 'bold',
    });
    scoreText.setOrigin(0.5);

    // Score label
    this.add.text(GAME.WIDTH / 2, 240, 'SCORE', {
      fontFamily: 'Arial',
      fontSize: '16px',
      color: '#636E72',
    }).setOrigin(0.5);

    // High score (if not new)
    if (!isNewHighScore && this.gameData.highScore > 0) {
      this.add.text(GAME.WIDTH / 2, 340, `Best: ${this.gameData.highScore}`, {
        fontFamily: 'Arial',
        fontSize: '18px',
        color: '#636E72',
      }).setOrigin(0.5);
    }

    // Animate score counting up
    const displayScore = { value: 0 };
    this.tweens.add({
      targets: displayScore,
      value: this.gameData.score,
      duration: 1000,
      ease: 'Quad.easeOut',
      onUpdate: () => {
        scoreText.setText(Math.floor(displayScore.value).toString());
      },
    });
  }

  /**
   * Create reason text (why game ended)
   */
  private createReasonText(): void {
    if (this.gameData.reason) {
      const color = this.gameData.isWin ? '#4CAF50' : '#FF5722';
      this.add.text(GAME.WIDTH / 2, 390, this.gameData.reason, {
        fontFamily: 'Arial',
        fontSize: '16px',
        color,
      }).setOrigin(0.5);
    }
  }

  /**
   * Create game statistics
   */
  private createStats(): void {
    this.add.text(GAME.WIDTH / 2, 450, `Pieces placed: ${this.gameData.piecesPlaced}`, {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#636E72',
    }).setOrigin(0.5);
  }

  /**
   * Create action buttons
   */
  private createButtons(): void {
    // Retry button
    const retryButton = this.createButton(
      GAME.WIDTH / 2,
      550,
      'PLAY AGAIN',
      '#2D3436',
      () => {
        this.scene.start(SCENES.GAME);
      }
    );

    // Menu button
    this.createButton(
      GAME.WIDTH / 2,
      620,
      'MENU',
      '#636E72',
      () => {
        this.scene.start(SCENES.MENU);
      },
      true
    );

    // Pulsing animation on retry button
    this.tweens.add({
      targets: retryButton,
      alpha: 0.7,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Create a button
   */
  private createButton(
    x: number,
    y: number,
    text: string,
    color: string,
    onClick: () => void,
    outline: boolean = false
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const width = 200;
    const height = 50;
    const radius = 12;

    const background = this.add.graphics();

    if (outline) {
      background.lineStyle(2, parseInt(color.replace('#', ''), 16), 1);
      background.strokeRoundedRect(-width / 2, -height / 2, width, height, radius);
    } else {
      background.fillStyle(parseInt(color.replace('#', ''), 16), 1);
      background.fillRoundedRect(-width / 2, -height / 2, width, height, radius);
    }

    const label = this.add.text(0, 0, text, {
      fontFamily: 'Arial',
      fontSize: '18px',
      color: outline ? color : '#FFFFFF',
      fontStyle: 'bold',
    });
    label.setOrigin(0.5);

    container.add([background, label]);

    // Make interactive
    container.setSize(width, height);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerover', () => {
      container.setScale(1.05);
    });

    container.on('pointerout', () => {
      container.setScale(1);
    });

    container.on('pointerdown', onClick);

    return container;
  }
}
